// Paso 07 — cobro de contado: monto con el folio de conciliación y hasta 3 formas de pago.
// El wizard no es la caja: el dinero entra a la caja de la tienda y el folio lo concilia.
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import { ArrowRight, Check } from 'lucide-react';
import { Button, Loading } from '@/kit';
import { cn } from '@/lib/cn';
import { formatMXN } from '@/shared/lib/format/money';
import { useFolio, useRegisterPayment, useWizardSession } from '../api/useWizard';
import { useCatalogLineInfo } from '../api/useCatalogLineInfo';
import { useChargeableLines } from '../api/useChargeableLines';
import { wizardErrorCode, wizardErrorMsg } from '../api/wizardError';
import { chargeFailure } from '../chargeErrors';
import { cashChargeCents, chargeLinesOf } from '../saleCharge';
import { ChargeBreakdown } from '../components/ChargeBreakdown';
import { lineDiscountsOf } from '../quoteBreakdown';
import type { LineSlots } from '../components/LineLabels';
import { UnchargeableLinesNotice } from '../components/UnchargeableLinesNotice';
import { ChargeReloadNotice } from '../components/ChargeReloadNotice';
import { PromotionRevalidationNotice } from '../components/PromotionRevalidationNotice';
import { PromotionPaymentNotice } from '../components/PromotionPaymentNotice';
import { pendingRevalidationLineIds } from '../promotionRevalidation';
import { WizardCard, WizardCardBody, WizardCardHead } from '../components/WizardCard';
import { WizardBackLink, WizardFootNav } from '../components/WizardFootNav';
import { InlineActionButton } from '../components/InlineActionButton';
import { PaymentListForm } from '../components/PaymentListForm';
import {
    balanceBlocker,
    initialPaymentDrafts,
    paymentsBalance,
    paymentsBlocker,
    toSalePayments,
    type PaymentDraft,
} from '../salePayments';
import { NO_CHARGE_LABEL, registeredMethodsLabel } from '../payments';
import type { RegisteredPayment } from '../types';
import { PaymentBreakdown } from '@/shared/patterns/SalePayments/PaymentBreakdown';
import { catalogDisplayName } from '@/shared/lib/format/itemDisplayName';

/** Piezas de Promociones del cobro: nombres en el desglose y copy del rechazo por medio de cobro. */
export interface PaymentSlots extends LineSlots {
    promotionChargeCopy?: (error: unknown) => string | undefined;
}

/** Leyenda del cobro registrado; el de $0 no tiene medio y no se nombra como uno. */
function registeredCopy(registered: RegisteredPayment): string {
    const label = registeredMethodsLabel(registered);
    return label ? `Cobrado con ${label}` : NO_CHARGE_LABEL;
}

/** .hint del mock: 11px muted, line-height 1.35. */
const HINT = 'text-[11px] leading-[1.35] text-muted-foreground';

export function PaymentStep({
    sessionId,
    slots = {},
}: {
    sessionId: string;
    /** Piezas de Promociones para nombrar regalos y promos en el desglose. */
    slots?: PaymentSlots;
}) {
    const navigate = useNavigate();
    const { data: session, refresh } = useWizardSession(sessionId);
    const lineInfo = useCatalogLineInfo(sessionId);
    const folioMut = useFolio(sessionId);
    const registerPayment = useRegisterPayment(sessionId);
    const chargeable = useChargeableLines(sessionId, session);

    const [drafts, setDrafts] = useState<readonly PaymentDraft[]>(initialPaymentDrafts);
    const [folioError, setFolioError] = useState<string>();
    /** Rechazo porque la venta cambió: queda en pantalla y frena el cobro hasta recargar. */
    const [reloadMessage, setReloadMessage] = useState<string>();
    /** Una promo no acepta el medio: queda en pantalla hasta cambiar el medio o quitar la promo. */
    const [promotionPaymentMessage, setPromotionPaymentMessage] = useState<string>();
    /** El 200 del cobro y el commit del `navigate` no ocurren en el mismo render. */
    const submitted = useRef(false);

    /** Sin reserva no hay cobro ni folio: la única salida es volver a agregar el producto. */
    const goToProduct = async (message: string): Promise<void> => {
        toast.error(message);
        await navigate({
            to: '/wizard/s/$sessionId/$step',
            params: { sessionId, step: 'product' },
        });
    };

    /** Una promo quedó sin revalidar: en Arma la venta el front la vuelve a aplicar. */
    const goToBuildSale = async (message: string): Promise<void> => {
        toast.error(message);
        void refresh();
        await navigate({
            to: '/wizard/s/$sessionId/$step',
            params: { sessionId, step: 'build-sale' },
        });
    };

    /** Pide el folio (idempotente). El botón de cobrar cuelga de él, así que fallar deja salida. */
    const requestFolio = async (): Promise<void> => {
        try {
            await folioMut.mutateAsync();
            setFolioError(undefined);
        } catch (e) {
            // El folio va detrás del guard de la reserva: sin ella el reintento siempre fallaría.
            const failure = chargeFailure(e);
            if (failure?.recovery === 'product') {
                await goToProduct(failure.message);
                return;
            }
            if (failure?.recovery === 'build-sale') {
                await goToBuildSale(failure.message);
                return;
            }
            if (failure?.recovery === 'reload') void refresh();
            setFolioError(
                failure?.message ?? wizardErrorMsg(e, 'No se pudo generar el folio de cobro.'),
            );
        }
    };

    // El folio se genera/lee al entrar (idempotente) y sobrevive. La venta ya cobrada no lo pide:
    // el silo exige folio para registrar el cobro, así que si hay cobro hay folio.
    const hasFolio = !!session?.folio;
    const paid = !!session?.payment;
    // Con una promo por revalidar el silo rechaza el folio: se pide al volver de Arma la venta.
    const revalidationPending = pendingRevalidationLineIds(session).length > 0;
    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect -- el aviso se escribe después del await, no durante el render
        if (session && !hasFolio && !paid && !revalidationPending) void requestFolio();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [session?.sessionId, hasFolio, paid, revalidationPending]);

    if (!session) return <Loading size="lg" />;
    const product = session.product;
    const registered = session.payment;
    const hasLines = session.reservedLines !== undefined;
    const lines = chargeLinesOf(session, { credit: false, ...lineInfo });
    // Cobrado manda el monto que de verdad entró a la caja; si no, la suma de las líneas.
    const amountCents = registered?.amountCents ?? cashChargeCents(session);
    const amount = formatMXN(amountCents / 100);

    const valid = paymentsBlocker(drafts, amountCents) === null;
    const balanceLabel =
        drafts.length > 1 ? balanceBlocker(paymentsBalance(drafts, amountCents)) : null;

    const goToDelivery = async (): Promise<void> => {
        await navigate({
            to: '/wizard/s/$sessionId/$step',
            params: { sessionId, step: 'delivery' },
        });
    };

    /** Cada motivo del silo manda al vendedor a algo distinto: re-reservar, reintentar o corregir. */
    const handleRegisterError = async (e: unknown): Promise<void> => {
        const code = wizardErrorCode(e);
        const failure = chargeFailure(e, slots.promotionChargeCopy);
        if (failure?.recovery === 'payment-method') {
            setPromotionPaymentMessage(failure.message);
            return;
        }
        if (failure?.recovery === 'product') {
            await goToProduct(failure.message);
            return;
        }
        if (failure?.recovery === 'build-sale') {
            await goToBuildSale(failure.message);
            return;
        }
        if (failure) {
            if (failure.recovery === 'reload') setReloadMessage(failure.message);
            toast.error(failure.message);
            return;
        }
        if (code === 'FOLIO_NOT_GENERATED') {
            setFolioError(wizardErrorMsg(e, 'No se pudo generar el folio de cobro.'));
            toast.error('Falta el folio de cobro — genéralo de nuevo antes de cobrar.');
            return;
        }
        // El silo solo manda este 409 si el cobro nuevo es DISTINTO al registrado; el repetido
        // idéntico devuelve 200. Su `msg` diría «ya fue registrado» y sonaría a paso cumplido.
        if (code === 'PAYMENT_ALREADY_REGISTERED') {
            void refresh();
            toast.error(
                'Esta venta ya tiene un cobro registrado con otros datos. Captura las formas de pago con las que se cobró para continuar.',
            );
            return;
        }
        toast.error(wizardErrorMsg(e, 'No se pudo registrar el cobro. Intenta de nuevo.'));
    };

    const handleRegister = async (): Promise<void> => {
        if (submitted.current) return;
        submitted.current = true;
        try {
            await registerPayment.mutateAsync({
                amountCents,
                payments: toSalePayments(drafts, amountCents),
            });
        } catch (e) {
            submitted.current = false;
            await handleRegisterError(e);
            return;
        }
        await goToDelivery();
    };

    return (
        <WizardCard>
            <WizardCardHead
                eyebrow="Cobro"
                title="Cobrar la venta"
                sub={
                    hasLines && lines.length > 0 ? (
                        <>
                            {lines.length === 1
                                ? '1 producto'
                                : `${String(lines.length)} productos`}{' '}
                            · {registered ? 'Cobrado' : 'Total'}:{' '}
                            <span className="tabular-nums">{amount}</span>
                        </>
                    ) : product ? (
                        <>
                            Equipo:{' '}
                            <strong className="font-semibold text-foreground">
                                {catalogDisplayName(product, lineInfo.descriptions)}
                            </strong>{' '}
                            · {registered ? 'Cobrado' : 'Precio'}:{' '}
                            <span className="tabular-nums">{amount}</span>
                        </>
                    ) : (
                        <span className="text-destructive">Falta elegir el teléfono.</span>
                    )
                }
            />

            <WizardCardBody className="flex flex-col gap-5">
                {/* .amount-hero — degradado navy con el monto y el folio de conciliación. */}
                <div className="rounded-xl bg-[linear-gradient(135deg,hsl(var(--primary)),hsl(222_47%_20%))] px-[22px] py-5 text-primary-foreground">
                    <div className="text-[10px] font-semibold uppercase tracking-[0.08em] opacity-[0.82]">
                        {paid ? 'Cobro registrado' : 'Total a cobrar'}
                    </div>
                    <div className="mt-1 text-[34px] font-bold leading-[1.05] tracking-[-0.02em] tabular-nums">
                        {amount}
                    </div>
                    <div className="mt-1 text-[12.5px] opacity-[0.88]">
                        {registered
                            ? registeredCopy(registered)
                            : hasLines
                              ? 'Suma de los productos de la venta'
                              : 'Precio del equipo de contado'}
                    </div>
                    <div className="mt-2 border-t border-primary-foreground/20 pt-2">
                        <div className="text-[9.5px] uppercase tracking-wider opacity-70">
                            Folio de cobro
                        </div>
                        <div className="font-mono text-[13px] font-semibold tracking-[0.02em]">
                            {session.folio ?? (folioError ? 'sin folio' : 'generando…')}
                        </div>
                        {folioError ? (
                            <div className="mt-1.5 flex flex-wrap items-center gap-2">
                                <span className="text-[11px] leading-[1.35] opacity-90">
                                    {folioError}
                                </span>
                                <InlineActionButton
                                    isLoading={folioMut.isPending}
                                    onClick={() => void requestFolio()}
                                    className="rounded-md border border-primary-foreground/40 px-2 py-1 text-[11px] font-semibold transition-colors hover:bg-primary-foreground/10"
                                >
                                    {folioMut.isPending ? 'Generando…' : 'Reintentar'}
                                </InlineActionButton>
                            </div>
                        ) : null}
                    </div>
                </div>

                {hasLines && lines.length > 0 && (
                    <ChargeBreakdown
                        slots={slots}
                        lines={lines}
                        discounts={lineDiscountsOf(session, lines)}
                        totalLabel="Total a cobrar"
                        totalCents={amountCents}
                    />
                )}

                {!registered && (
                    <>
                        {revalidationPending && (
                            <PromotionRevalidationNotice sessionId={sessionId} />
                        )}
                        <PromotionPaymentNotice
                            sessionId={sessionId}
                            message={promotionPaymentMessage}
                        />
                        <ChargeReloadNotice
                            message={reloadMessage}
                            onReload={async () => {
                                await refresh();
                                setReloadMessage(undefined);
                            }}
                        />
                        <UnchargeableLinesNotice
                            lines={chargeable}
                            onReload={() => void refresh()}
                            onFixLost={() => {
                                void navigate({
                                    to: '/wizard/s/$sessionId/$step',
                                    params: { sessionId, step: 'build-sale' },
                                });
                            }}
                        />
                    </>
                )}

                {registered ? (
                    /* ── Hecho consumado: el dinero ya entró, no hay nada que cobrar ── */
                    <div className="rounded-lg border border-border bg-card p-4">
                        <div className="flex items-center gap-2">
                            <span className="flex size-6 flex-none items-center justify-center rounded-full bg-success/15 text-success">
                                <Check className="size-3.5" strokeWidth={3} aria-hidden />
                            </span>
                            <p className="text-[13px] font-semibold">
                                Cobro registrado —{' '}
                                {registeredMethodsLabel(registered) ?? NO_CHARGE_LABEL}
                            </p>
                        </div>
                        {registered.payments && registered.payments.length > 1 && (
                            <PaymentBreakdown lines={registered.payments} className="mt-3" />
                        )}
                        <p className={cn(HINT, 'mt-2')}>
                            El cliente ya pagó{' '}
                            <strong className="font-semibold tabular-nums">{amount}</strong>. No
                            vuelvas a cobrarle: continúa con la entrega del equipo.
                        </p>
                    </div>
                ) : amountCents === 0 ? (
                    <p className={HINT}>No hay nada que cobrar: confirma para continuar.</p>
                ) : (
                    <PaymentListForm
                        drafts={drafts}
                        totalCents={amountCents}
                        onChange={(next) => {
                            setDrafts(next);
                            setPromotionPaymentMessage(undefined);
                        }}
                    />
                )}
            </WizardCardBody>

            <WizardFootNav back={<WizardBackLink sessionId={sessionId} step="payment-method" />}>
                {registered ? (
                    <Button
                        rightIcon={<ArrowRight className="size-3.5" aria-hidden />}
                        onClick={() => void goToDelivery()}
                    >
                        Continuar a Entrega
                    </Button>
                ) : (
                    <Button
                        disabled={
                            !valid ||
                            !session.folio ||
                            chargeable.blocked ||
                            !!reloadMessage ||
                            revalidationPending
                        }
                        isLoading={registerPayment.isPending}
                        rightIcon={<ArrowRight className="size-3.5" aria-hidden />}
                        onClick={() => void handleRegister()}
                    >
                        {balanceLabel ?? 'Confirmar cobro y continuar'}
                    </Button>
                )}
            </WizardFootNav>
        </WizardCard>
    );
}
